import { type FC, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useOpenDocumentMutation } from "web_myplann/Api/Document/useOpenDocumentMutation";
import type {
	ClientPortfolioDetailRelatedResponseDTO,
	PortfolioDocumentListItemDTO,
} from "web_myplann/Infrastructure/Api/Api";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DetailRemindersSection } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailRemindersSection";
import { PortfolioRelatedPinDrawer } from "web_myplann/Portfolio/Components/Redesign/Detail/Pin/PortfolioRelatedPinDrawer";
import { PropertyRelatedPinDrawer } from "web_myplann/Portfolio/Components/Redesign/Detail/Pin/PropertyRelatedPinDrawer";
import type {
	PinKind,
	RelatedPinKind,
} from "web_myplann/Portfolio/Components/Redesign/Detail/Pin/pinKind";
import { RelatedCard } from "web_myplann/Portfolio/Components/Redesign/Detail/RelatedCard";
import { getReminderEntity } from "web_myplann/Portfolio/Helpers/getReminderEntity";
import {
	relatedByPinKind,
	relatedTitle,
} from "web_myplann/Portfolio/Helpers/relatedByPinKind";
import { PortfolioItemKind } from "web_myplann/Portfolio/Types/portfolioItemKind";

type Props = {
	related: ClientPortfolioDetailRelatedResponseDTO[];
	documents: PortfolioDocumentListItemDTO[] | undefined;
	showActions: boolean;
} & (
	| { itemKind: PortfolioItemKind.Portfolio; portfolioId: number }
	| { itemKind: PortfolioItemKind.Property; propertyId: number }
);

export const RelatedSectionRedesign: FC<Props> = (props) => {
	const { related, documents = [], showActions } = props;
	const navigate = useNavigate();
	const { t } = useResource();
	const { mutate: openDocument } = useOpenDocumentMutation();
	const [pinKind, setPinKind] = useState<RelatedPinKind>("contract");
	const [isPinOpen, setIsPinOpen] = useState(false);

	const openPin = (kind: PinKind) => {
		if (kind === "document") {
			toast(t(Resources.DataCard.documentPinUnavailableToast));
			return;
		}

		setPinKind(kind);
		setIsPinOpen(true);
	};

	const relatedContracts = relatedByPinKind(related, "contract");
	const relatedProperties = relatedByPinKind(related, "property");
	const relatedServices = relatedByPinKind(related, "service");

	const openRelatedPortfolio = (
		item: ClientPortfolioDetailRelatedResponseDTO,
	) =>
		navigate(
			Routing.Portfolio.PortfolioDetail.path(
				item.relatedPortfolio?.myplannPortfolioId?.toString() ?? "",
			),
		);

	return (
		<>
			<RelatedCard
				kind="document"
				items={documents}
				getLabel={(document) => document.title}
				onItemClick={(document) =>
					openDocument({
						id: document.id,
						fallbackTitle: document.title ?? undefined,
					})
				}
				onPin={openPin}
				showActions={showActions}
			/>

			<DetailRemindersSection
				entity={getReminderEntity(
					props.itemKind,
					props.itemKind === PortfolioItemKind.Property
						? props.propertyId
						: props.portfolioId,
				)}
			/>

			<RelatedCard
				kind="contract"
				items={relatedContracts}
				getLabel={(item) => relatedTitle(item, "contract", t)}
				getIconSource={(item) => ({ domain: item.relatedPortfolio?.domain })}
				onItemClick={openRelatedPortfolio}
				onPin={openPin}
				showActions={showActions}
			/>

			<RelatedCard
				kind="property"
				items={relatedProperties}
				getLabel={(item) => relatedTitle(item, "property", t)}
				getIconSource={(item) => ({ propertyType: item.relatedProperty?.type })}
				onItemClick={(item) =>
					navigate(
						Routing.Portfolio.PropertyDetail.path(
							item.relatedProperty?.myplannPropertyId?.toString() ?? "",
						),
					)
				}
				onPin={openPin}
				showActions={showActions}
			/>

			<RelatedCard
				kind="service"
				items={relatedServices}
				getLabel={(item) => relatedTitle(item, "service", t)}
				getIconSource={(item) => ({ domain: item.relatedPortfolio?.domain })}
				onItemClick={openRelatedPortfolio}
				onPin={openPin}
				showActions={showActions}
			/>

			{props.itemKind === PortfolioItemKind.Property ? (
				<PropertyRelatedPinDrawer
					kind={pinKind}
					open={isPinOpen}
					onClose={() => setIsPinOpen(false)}
					related={related}
					propertyId={props.propertyId}
				/>
			) : (
				<PortfolioRelatedPinDrawer
					kind={pinKind}
					open={isPinOpen}
					onClose={() => setIsPinOpen(false)}
					related={related}
					portfolioId={props.portfolioId}
				/>
			)}
		</>
	);
};
